import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@bpinternal/ui-kit-next'
import { Code2, Loader2, PanelsTopLeft, Plus, WandSparkles } from 'lucide-react'
import type { ComponentProps } from 'react'
import { useCreateBotFlow } from '~/hooks/bots'

import type { BotType } from './BotTypeSelectionDialog'

export const BOT_TYPE_MENU_ITEMS = {
  vibe: {
    label: 'Viber bot',
    description: 'Build with an AI builder',
    Icon: WandSparkles,
  },
  studio: {
    label: 'Studio bot',
    description: 'Build visually on the canvas',
    Icon: PanelsTopLeft,
  },
  adk: {
    label: 'ADK bot',
    description: 'Build in TypeScript',
    Icon: Code2,
  },
} as const satisfies Record<
  BotType,
  {
    label: string
    description: string
    Icon: typeof WandSparkles
  }
>

type BotTypeMenuItemsProps = {
  botTypes: readonly BotType[]
  onSelect: (botType: BotType) => void
}

export function BotTypeMenuItems({ botTypes, onSelect }: BotTypeMenuItemsProps) {
  return botTypes.map((botType) => {
    const { label, description, Icon } = BOT_TYPE_MENU_ITEMS[botType]

    return (
      <DropdownMenuItem key={botType} className="min-w-56 items-start gap-3" onClick={() => onSelect(botType)}>
        <Icon className="mt-0.5 h-4 w-4 text-muted-foreground" />
        <span className="flex flex-col">
          <span>{label}</span>
          <span className="text-xs text-muted-foreground">{description}</span>
        </span>
      </DropdownMenuItem>
    )
  })
}

type CreateBotButtonProps = Omit<ComponentProps<typeof Button>, 'children' | 'onClick'> & {
  workspaceId: string | undefined
}

export function CreateBotButton({ workspaceId, disabled, ...buttonProps }: CreateBotButtonProps) {
  const { startCreateBotFlow, isCreatingBot, botTypes, useCreateBotDropdown } = useCreateBotFlow({ workspaceId })
  const isDisabled = disabled || isCreatingBot

  const content = (
    <>
      {isCreatingBot ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus />}
      {isCreatingBot ? 'Creating...' : 'Create Bot'}
    </>
  )

  if (!useCreateBotDropdown || botTypes.length <= 1) {
    return (
      <Button {...buttonProps} disabled={isDisabled} onClick={() => startCreateBotFlow()}>
        {content}
      </Button>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button {...buttonProps} disabled={isDisabled}>
          {content}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <BotTypeMenuItems botTypes={botTypes} onSelect={startCreateBotFlow} />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
